import { useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { imagesApi } from '@/api/endpoints/images'
import { Button } from '@/components/ui/button'

interface ImageUploadFieldProps {
  value?: string
  onChange: (url: string | undefined) => void
  label: string
  description?: string
  disabled?: boolean
}

/**
 * Reusable single-image upload field.
 *
 * Stores a `media/<id>` reference via {@link onChange} rather than a URL — delivery URLs are built
 * server-side at read time, so they differ per environment and per width. {@link value} may also be
 * an absolute URL, which is the historical form and still displayed as-is.
 */
export function ImageUploadField({
  value,
  onChange,
  label,
  description,
  disabled,
}: ImageUploadFieldProps) {
  const { t } = useTranslation('restaurants')
  const inputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useMutation({
    mutationFn: (file: File) => imagesApi.uploadAdminImage(file),
    onSuccess: ({ ref }) => {
      onChange(ref)
      toast.success(t('form.images.uploadSuccess'))
    },
    onError: () => toast.error(t('form.images.uploadError')),
  })

  const busy = uploadMutation.isPending
  const previewUrl = imagesApi.mediaPreviewUrl(value)

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">{label}</div>
      {description && (
        <div className="text-xs text-muted-foreground">{description}</div>
      )}
      <div className="flex items-center gap-3">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className="size-16 rounded-md object-cover"
          />
        ) : (
          <div className="flex size-16 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
            {t('form.images.none')}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) uploadMutation.mutate(file)
              e.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy
              ? t('form.images.uploading')
              : value
                ? t('form.images.replace')
                : t('form.images.upload')}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || busy}
              onClick={() => onChange(undefined)}
            >
              {t('form.images.remove')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
